import * as React from 'react';

import {
  ANY_COMPLETION_OPTION,
  COMPLETION_OPTIONS,
  GROUP_BY_OPTIONS,
  SORT_OPTIONS,
} from './filters';
import { CARD_PARTS, CardPart } from './useShownCardParts';
import { useStickyFiltersHeight } from './useStickyFiltersHeight';

import { DropdownInput, EXTRA_SMALL_ICON_BASE_SRC, SMALL_ICON_BASE_SRC, SegmentedControl, Tooltip } from '../../../../Shared/index';

const LIST_VIEW_LABEL = 'Detailed'
const GRID_VIEW_LABEL = 'Compact'
const GROUP_BY_SEARCHING_NOTE = 'Search results show as one list. Clear the search to group them again.'

function summaryOfShownParts(shownParts: CardPart[]): string {
  const labels = CARD_PARTS.filter(part => shownParts.includes(part.value)).map(part => part.label)
  if (labels.length === CARD_PARTS.length) { return 'All' }
  if (!labels.length) { return 'None' }
  return labels.join(', ')
}

interface FilterRowProps {
  searchQuery: string;
  onSearchQueryChange: (searchQuery: string) => void;
  groupBy: string;
  onGroupByChange: (groupBy: string) => void;
  sortBy: string;
  onSortChange: (sortBy: string) => void;
  selectedCompletion: string;
  onCompletionChange: (completion: string) => void;
  shownParts: CardPart[];
  onShownPartsChange: (parts: CardPart[]) => void;
  isListView: boolean;
  onShowList: () => void;
  onShowGrid: () => void;
}

const FilterRow = ({ searchQuery, onSearchQueryChange, groupBy, onGroupByChange, sortBy, onSortChange, selectedCompletion, onCompletionChange, shownParts, onShownPartsChange, isListView, onShowList, onShowGrid }: FilterRowProps) => {
  const searchIconSrc = `${SMALL_ICON_BASE_SRC}/search.svg`
  const closeIconSrc = `${SMALL_ICON_BASE_SRC}/close.svg`
  const barRef = React.useRef<HTMLElement>(null)
  useStickyFiltersHeight(barRef)

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) { onSearchQueryChange(e.target.value) }
  function clearSearch() { onSearchQueryChange('') }
  function handleGroupByOption(option: { value: string }) { onGroupByChange(option.value) }
  function handleSortOption(option: { value: string }) { onSortChange(option.value) }
  function handleCompletionOption(option: { value: string }) { onCompletionChange(option.value) }
  function handleShownPartsOptions(options: Array<{ value: CardPart }> | null) { onShownPartsChange((options || []).map(option => option.value)) }

  const shownPartsSummary = summaryOfShownParts(shownParts)
  const isSearching = searchQuery !== ''
  const groupByDropdown = (
    <DropdownInput
      ariaLabel="Group by"
      className="small bordered focus-on-light"
      disabled={isSearching}
      handleChange={handleGroupByOption}
      isSearchable={true}
      label="Group by"
      options={GROUP_BY_OPTIONS}
      value={GROUP_BY_OPTIONS.find(option => option.value === groupBy) || GROUP_BY_OPTIONS[0]}
    />
  )

  return (
    <section className="manage-activities-v2-filters" ref={barRef}>
      <div className="container">
        <div className="filter-row">
          <div className="search-wrapper">
            <label className="search-label" htmlFor="pack-search">Search</label>
            <div className="search-input-wrap">
              {/* invisible copy of the placeholder: it sets the wrapper's intrinsic width */}
              <span aria-hidden="true" className="search-sizer">Pack or activity name</span>
              <img alt="" className="search-icon" src={searchIconSrc} />
              <input
                aria-label="Search packs or activities"
                className="focus-on-light"
                id="pack-search"
                onChange={handleSearchChange}
                placeholder="Pack or activity name"
                type="search"
                value={searchQuery}
              />
              {searchQuery && (
                <button aria-label="Clear search" className="clear-search focus-on-light" onClick={clearSearch} type="button">
                  <img alt="" src={closeIconSrc} />
                </button>
              )}
            </div>
          </div>
          {isSearching ? <Tooltip tooltipText={GROUP_BY_SEARCHING_NOTE} tooltipTriggerText={groupByDropdown} /> : groupByDropdown}
          <DropdownInput
            ariaLabel="Sort by"
            className="small bordered focus-on-light"
            handleChange={handleSortOption}
            isSearchable={true}
            label="Sort by"
            options={SORT_OPTIONS}
            value={SORT_OPTIONS.find(option => option.value === sortBy) || SORT_OPTIONS[0]}
          />
          <DropdownInput
            ariaLabel="Status"
            className="small bordered focus-on-light"
            handleChange={handleCompletionOption}
            isSearchable={true}
            label="Status"
            options={COMPLETION_OPTIONS}
            value={COMPLETION_OPTIONS.find(option => option.value === selectedCompletion) || ANY_COMPLETION_OPTION}
          />
          <div className="density-toggle">
            <SegmentedControl
              activeTab={isListView ? LIST_VIEW_LABEL : GRID_VIEW_LABEL}
              buttons={[
                {
                  label: LIST_VIEW_LABEL,
                  labelIcon: <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/grid-rectangle-1x2.svg`} />,
                  onClick: onShowList,
                },
                {
                  label: GRID_VIEW_LABEL,
                  labelIcon: <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/grid-rectangle-3x2.svg`} />,
                  onClick: onShowGrid,
                },
              ]}
              label="View"
              size="small"
            />
          </div>
          <DropdownInput
            ariaLabel="Customize view"
            className="small bordered focus-on-light"
            handleChange={handleShownPartsOptions}
            isMulti={true}
            label="Customize view"
            options={CARD_PARTS}
            selectProps={{ summaryText: shownPartsSummary }}
            showAllOption={false}
            value={CARD_PARTS.filter(part => shownParts.includes(part.value))}
          />
        </div>
      </div>
    </section>
  )
}

export default FilterRow
